import React, { useState } from 'react';
import { Menu } from 'lucide-react';
import { useSettings } from '../../hooks/useSettings';

// Custom hooks
import { useUserManagerNavigation } from '../../hooks/admin/useUserManagerNavigation';
import { useAdminData } from '../../hooks/admin/useAdminData';
import { useSubscriptionPlans } from '../../hooks/admin/useSubscriptionPlans';
import { useUserManagement } from '../../hooks/admin/useUserManagement';

// Components
import UserManagerSidebar from '../admin/UserManagerSidebar';
import UserManagerTabRenderer from './UserManagerTabRenderer';

/**
 * UserManagerDashboard - Limited admin interface for USER_MANAGER role
 * Similar structure to AdminDashboard but with restricted menu and features
 */
const UserManagerDashboard = () => {
  // Mobile sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Navigation hook for user manager
  const { activeTab, handleTabChange, handleLogout } = useUserManagerNavigation();
  
  // Data management hooks (reuse existing hooks)
  const adminData = useAdminData(activeTab);
  const subscriptionPlans = useSubscriptionPlans(activeTab);
  const userManagement = useUserManagement();
  
  // App settings
  const { 
    settings: appSettings, 
    loading: appSettingsLoading, 
    settingsLoaded: appSettingsLoaded, 
    safeSettings: appSafeSettings 
  } = useSettings();
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="mobile-header md:hidden">
        <button
          onClick={() => setSidebarOpen(true)}
          className="bg-white/10 hover:bg-white/20 rounded p-2 transition-colors"
        >
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-2 ml-3">
          {appSettings?.product_logo && (
            <img src={appSettings.product_logo} alt="Logo" className="h-6 object-contain" />
          )}
          <span className="text-lg font-bold">{appSafeSettings?.product_name || 'User Manager'}</span>
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="mobile-overlay md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <UserManagerSidebar
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        handleLogout={handleLogout}
        appSettings={appSettings}
        appSettingsLoaded={appSettingsLoaded}
        appSafeSettings={appSafeSettings}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      {/* Main Content */}
      <div className="admin-content ml-80 min-h-screen mobile-content">
        <UserManagerTabRenderer
          activeTab={activeTab}
          adminData={adminData}
          subscriptionPlans={subscriptionPlans}
          userManagement={userManagement}
          appSettings={appSettings}
        />
      </div>
    </div>
  );
};

export default UserManagerDashboard;